import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAllIn } from '../lib/adminCounts';
import { onlyUpcoming } from '../../lib/eventDates';
import { COUNT_TIPS } from '../lib/adminCounts';
import { fetchAllRows } from '../../lib/fetchAll';
import InfoTip from '../components/InfoTip';
import { logAudit } from '../lib/auditLog';

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDate(dateStr) {
  if (!dateStr) return 'Date TBD';
  try {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return dateStr; }
}

async function enrichWithUsernames(events) {
  if (!events?.length) return events;
  const userIds = [...new Set(events.map(e => e.user_id).filter(Boolean))];
  if (!userIds.length) return events;
  const profiles = await fetchAllIn('user_profiles', 'id, user_id, username', 'user_id', userIds);
  const map = {};
  (profiles || []).forEach(p => { map[p.user_id] = p.username; });
  return events.map(e => ({ ...e, username: map[e.user_id] || 'reader' }));
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
function Pill({ children, color = 'gray' }) {
  const styles = {
    gray:  { background: 'var(--brand-cream)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' },
    green: { background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' },
    amber: { background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)' },
    red:   { background: 'var(--feedback-error-bg)', color: '#991b1b', border: '1px solid #fca5a5' },
    rust:  { background: '#fff1ee', color: '#9a3412', border: '1px solid #fdba74' },
  };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', fontSize: 'var(--font-size-xxs)',
      fontWeight: 700, padding: '3px 9px', borderRadius: 'var(--radius-xxl)',
      letterSpacing: 0.2, whiteSpace: 'nowrap', flexShrink: 0,
      ...styles[color],
    }}>
      {children}
    </span>
  );
}

function Btn({ children, onClick, variant = 'default', disabled }) {
  const variants = {
    default: { background: 'var(--brand-white)',    color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' },
    approve: { background: 'var(--feedback-success-bg)', color: '#065f46', border: '1px solid #86efac' },
    reject:  { background: '#fff8f8', color: '#991b1b', border: '1px solid #fca5a5' },
    amber:   { background: '#fffbeb', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      fontSize: 11.5, fontWeight: 600, padding: '6px 13px', borderRadius: 'var(--radius-md)',
      cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)',
      whiteSpace: 'nowrap', opacity: disabled ? 0.5 : 1, ...variants[variant],
    }}>
      {children}
    </button>
  );
}

const ROLE_META = {
  hosting:    { label: 'Hosting',    color: 'rust',  icon: '📣' },
  attending:  { label: 'Attending',  color: 'green', icon: '🙋' },
  going:      { label: 'Going',      color: 'green', icon: '🙋' },
  interested: { label: 'Interested', color: 'gray',  icon: '👀' },
};

// 'all' first, then the role values stored on profile_events.role.
const ROLE_KEYS = ['all', ...Object.keys(ROLE_META)];

// ══════════════════════════════════════════════════════════════════════════════
// FLAGGED QUEUE
// ══════════════════════════════════════════════════════════════════════════════
function FlaggedQueue({ onCountChange, isMobile }) {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const onCountRef = useRef(onCountChange);
  useEffect(() => { onCountRef.current = onCountChange; }, [onCountChange]);

  const load = useCallback(async () => {
    setLoading(true);
    // Paged: the Flagged card and the tab badge are both this array's length.
    const data = await fetchAllRows(() => supabase
      .from('profile_events')
      .select('id, name, date, time, venue, address, description, role, emoji, color, flagged, flag_reason, flag_count, created_at, lat, lng, user_id')
      .eq('flagged', true));
    data.sort((a, b) => (b.flag_count || 0) - (a.flag_count || 0));
    const error = null;
    if (error) { console.error('FlaggedQueue:', error.message); setLoading(false); return; }
    const enriched = await enrichWithUsernames(data || []);
    setEvents(enriched);
    onCountRef.current?.(enriched.length);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // None of these three checked their writes, and none of them has a try/catch
  // either — so a refused delete left the event on the reader's profile, wrote
  // an audit entry saying it was removed, and dropped the flag from this screen.
  // The moderator sees it gone; the reader still sees it live.
  async function handleDismiss(event) {
    setWorking(event.id);
    const { error } = await supabase.from('profile_events').update({ flagged: false, flag_reason: null, flag_count: 0 }).eq('id', event.id);
    if (error) { alert(`Flag NOT dismissed, nothing changed: ${error.message}`); setWorking(null); return; }
    await logAudit('update', 'event', `Dismissed flag on "${event.name}" by @${event.username}`, event.id);
    load(); setWorking(null);
  }

  async function handleRemove(event) {
    if (!confirm(`Remove "${event.name}" by @${event.username}? This also removes the community post.`)) return;
    setWorking(event.id);
    const { error: evErr } = await supabase.from('profile_events').delete().eq('id', event.id);
    if (evErr) { alert(`Event NOT removed, nothing changed: ${evErr.message}`); setWorking(null); return; }
    // The event is already gone at this point, so a failure here is a partial
    // removal and has to say so rather than being folded into one message.
    const { error: postErr } = await supabase.from('posts').delete().eq('post_type', 'event').contains('event_data', { profile_event_id: String(event.id) });
    if (postErr) alert(`Event removed, but its community post is STILL UP, delete it by hand: ${postErr.message}`);
    await logAudit('delete', 'event', `Removed flagged event "${event.name}" by @${event.username}${postErr ? ' (community post not removed)' : ''}`, event.id);
    load(); setWorking(null);
  }

  async function handleWarn(event) {
    setWorking(event.id);
    const { error } = await supabase.from('profile_events').update({ flagged: false, flag_reason: null, flag_count: 0 }).eq('id', event.id);
    if (error) { alert(`Flag NOT cleared, nothing changed: ${error.message}`); setWorking(null); return; }
    await logAudit('update', 'event', `Warned @${event.username} and dismissed flag on "${event.name}"`, event.id);
    load(); setWorking(null);
  }

  if (loading) return <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading flagged events...</div>;
  if (!events.length) return (
    <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
      ✓ No flagged events, all clear
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      {events.map(event => {
        const busy   = working === event.id;
        const urgent = (event.flag_count || 0) >= 2;
        const role   = ROLE_META[event.role] || ROLE_META.going;
        return (
          <div key={event.id} style={{
            background: urgent ? '#fff9f9' : '#fffdf9',
            border: '1px solid var(--brand-sand)',
            borderLeft: `3px solid ${urgent ? '#dc2626' : '#f59e0b'}`,
            borderRadius: 'var(--radius-lg)', padding: isMobile ? '12px 14px' : '16px 18px',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-10)', marginBottom: 'var(--space-10)' }}>
              <div style={{
                width: 36, height: 36, borderRadius: 'var(--radius-circle)', flexShrink: 0,
                background: urgent ? 'var(--feedback-error-bg)' : 'var(--feedback-warning-bg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xl)',
              }}>
                {event.emoji || '📍'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--neutral-800)' }}>{event.name || 'Untitled event'}</div>
                  <Pill color={urgent ? 'red' : 'amber'}>
                    {event.flag_reason || (urgent ? 'suspicious' : 'incomplete')}
                  </Pill>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--neutral-500)', marginTop: 'var(--space-xxs)' }}>
                  By <strong>@{event.username}</strong> · {timeAgo(event.created_at)} · flagged by{' '}
                  <strong style={{ color: 'var(--feedback-error)' }}>{event.flag_count || 1} reader{(event.flag_count || 1) !== 1 ? 's' : ''}</strong>
                </div>
              </div>
            </div>

            {/* Meta grid */}
            <div style={{
              background: 'var(--brand-paper)', border: '1px solid var(--brand-cream)', borderRadius: 'var(--radius-md)',
              padding: '10px 14px', marginBottom: 'var(--space-10)', fontSize: 'var(--font-size-sm)',
              display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '5px 14px',
            }}>
              <span style={{ color: 'var(--neutral-400)', fontWeight: 600 }}>Date</span>
              <span><strong>{formatDate(event.date)}</strong>{event.time ? ` · ${event.time}` : ''}</span>
              <span style={{ color: 'var(--neutral-400)', fontWeight: 600 }}>Location</span>
              <span>{event.venue || event.address || <em style={{ color: 'var(--neutral-400)' }}>not set</em>}</span>
              {event.lat && (
                <>
                  <span style={{ color: 'var(--neutral-400)', fontWeight: 600 }}>Coords</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-xs)' }}>{parseFloat(event.lat).toFixed(4)}, {parseFloat(event.lng).toFixed(4)}</span>
                </>
              )}
              {event.description && (
                <>
                  <span style={{ color: 'var(--neutral-400)', fontWeight: 600 }}>Description</span>
                  <span style={{ fontStyle: 'italic', color: 'var(--neutral-600)' }}>{event.description}</span>
                </>
              )}
              <span style={{ color: 'var(--neutral-400)', fontWeight: 600 }}>Role</span>
              <span>{role.icon} {role.label}</span>
            </div>

            {/* Actions — wrap on mobile */}
            <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
              <Btn variant="approve" onClick={() => handleDismiss(event)} disabled={busy}>Dismiss flag</Btn>
              <Btn variant="amber"   onClick={() => handleWarn(event)}    disabled={busy}>Warn user</Btn>
              <Btn variant="reject"  onClick={() => handleRemove(event)}  disabled={busy}>Remove event</Btn>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ALL EVENTS
// ══════════════════════════════════════════════════════════════════════════════
function AllEvents({ isMobile }) {
  const [events, setEvents]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [working, setWorking]       = useState(null);
  const [search, setSearch]         = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showPast, setShowPast]     = useState(false);
  const [counts, setCounts]         = useState({});
  const showPastRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('profile_events')
      .select('id, name, date, time, venue, address, description, role, emoji, color, flagged, flag_reason, flag_count, created_at, user_id')
      .order('date', { ascending: true })
      .limit(200);
    // Same rule as everywhere else: Pacific today, and a multi-day event stays
    // upcoming until its end_date. The .gte('date', today) this replaces used
    // UTC and ignored end_date, so this list and the "Upcoming" card above it
    // could disagree with each other.
    if (!showPastRef.current) query = onlyUpcoming(query);
    const { data, error } = await query;
    if (error) { console.error('AllEvents:', error.message); setLoading(false); return; }
    const enriched = await enrichWithUsernames(data || []);
    setEvents(enriched);

    // Role counts are totals, so they are counted in the database against the
    // same date filter as the list. Reading them off `enriched` reported the
    // make-up of the first 200 rows, which stops being the truth as soon as
    // there are more than 200 events.
    const roleCounts = {};
    await Promise.all(ROLE_KEYS.map(async role => {
      let q = supabase.from('profile_events').select('*', { count: 'exact', head: true });
      if (role !== 'all') q = q.eq('role', role);
      if (!showPastRef.current) q = onlyUpcoming(q);
      const { count } = await q;
      roleCounts[role] = count || 0;
    }));
    setCounts(roleCounts);

    setLoading(false);
  }, []);

  useEffect(() => { showPastRef.current = showPast; load(); }, [load, showPast]);

  async function handleRemove(event) {
    if (!confirm(`Remove "${event.name}" by @${event.username}?`)) return;
    setWorking(event.id);
    const { error: evErr } = await supabase.from('profile_events').delete().eq('id', event.id);
    if (evErr) { alert(`Event NOT removed, nothing changed: ${evErr.message}`); setWorking(null); return; }
    const { error: postErr } = await supabase.from('posts').delete().eq('post_type', 'event').contains('event_data', { profile_event_id: String(event.id) });
    if (postErr) alert(`Event removed, but its community post is STILL UP, delete it by hand: ${postErr.message}`);
    await logAudit('delete', 'event', `Admin removed "${event.name}" by @${event.username}${postErr ? ' (community post not removed)' : ''}`, event.id);
    load(); setWorking(null);
  }

  async function handleFlag(event) {
    setWorking(event.id);
    const { error } = await supabase.from('profile_events').update({ flagged: true, flag_count: (event.flag_count || 0) + 1, flag_reason: 'Flagged by admin' }).eq('id', event.id);
    if (error) { alert(`Event NOT flagged, nothing changed: ${error.message}`); setWorking(null); return; }
    await logAudit('update', 'event', `Admin flagged "${event.name}"`, event.id);
    load(); setWorking(null);
  }

  const ROLES = ROLE_KEYS;

  const filtered = events.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (e.name || '').toLowerCase().includes(q) ||
      (e.username || '').toLowerCase().includes(q) ||
      (e.venue || '').toLowerCase().includes(q) ||
      (e.address || '').toLowerCase().includes(q);
    return matchSearch && (roleFilter === 'all' || e.role === roleFilter);
  });

  return (
    <div>
      <input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search events..."
        style={{ width: '100%', padding: '8px 14px', fontSize: 12.5, boxSizing: 'border-box', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', outline: 'none', fontFamily: 'var(--font-body)', marginBottom: 'var(--space-md)' }}
      />

      {/* Filters — scrollable on mobile */}
      <div style={{ display: 'flex', gap: 'var(--space-6)', marginBottom: 'var(--space-14)', flexWrap: isMobile ? 'nowrap' : 'wrap', alignItems: 'center', overflowX: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch', paddingBottom: isMobile ? 4 : 0 }}>
        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}>Role:</span>
        {ROLES.map(r => {
          const count = counts[r] ?? 0;
          return (
            <button key={r} onClick={() => setRoleFilter(r)} style={{
              fontSize: 11.5, fontWeight: 600, padding: '4px 12px', borderRadius: 14,
              cursor: 'pointer', fontFamily: 'var(--font-body)', textTransform: 'capitalize',
              flexShrink: 0,
              background: roleFilter === r ? 'var(--neutral-800)' : 'var(--brand-white)',
              color:      roleFilter === r ? 'var(--brand-white)'    : 'var(--neutral-500)',
              border:     roleFilter === r ? '1px solid var(--neutral-800)' : '1px solid var(--brand-sand)',
            }}>
              {r}{count > 0 ? ` (${count})` : ''}
            </button>
          );
        })}
        <button onClick={() => setShowPast(p => !p)} style={{
          fontSize: 11.5, fontWeight: 600, padding: '4px 12px', borderRadius: 14,
          cursor: 'pointer', fontFamily: 'var(--font-body)',
          flexShrink: 0, marginLeft: isMobile ? 0 : 'auto',
          background: showPast ? 'var(--feedback-warning-bg)' : 'var(--brand-white)',
          color: showPast ? 'var(--feedback-warning-ink)' : 'var(--neutral-500)',
          border: showPast ? '1px solid var(--feedback-warning-border)' : '1px solid var(--brand-sand)',
        }}>
          {showPast ? '📅 Including past' : '📅 Upcoming only'}
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading events...</div>
      ) : !filtered.length ? (
        <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
          {events.length === 0
            ? showPast ? 'No events in the database' : 'No upcoming events, try "Including past"'
            : 'No events match your filters'}
        </div>
      ) : filtered.map((event, idx) => {
        const busy = working === event.id;
        const last = idx === filtered.length - 1;

        return (
          <div key={event.id} style={{
            padding: '12px 0',
            borderBottom: last ? 'none' : '1px solid var(--brand-cream)',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-md)' }}>
              {/* Icon */}
              <div style={{
                width: 36, height: 36, borderRadius: 'var(--radius-circle)', flexShrink: 0,
                background: event.flagged ? 'var(--feedback-error-bg)' : 'var(--feedback-warning-bg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-lg)',
              }}>
                {event.emoji || '📍'}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--neutral-800)', marginBottom: 'var(--space-xxs)' }}>
                  {event.name || 'Untitled event'}
                  {event.flagged && (
                    <span style={{ marginLeft: 'var(--space-sm)', fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--radius-10)', background: 'var(--feedback-error-bg)', color: '#991b1b' }}>
                      🚩 flagged
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--neutral-500)', marginBottom: 'var(--space-xs)' }}>
                  {event.role === 'hosting' ? 'Hosted by' : 'Posted by'} <strong>@{event.username}</strong>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-10)', fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', flexWrap: 'wrap', marginBottom: 'var(--space-sm)' }}>
                  <span>📅 {formatDate(event.date)}{event.time ? ` · ${event.time}` : ''}</span>
                  {(event.venue || event.address) && <span>📍 {event.venue || event.address}</span>}
                </div>

                {/* Actions below info on mobile, inline on desktop */}
                <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                  {!event.flagged && (
                    <Btn variant="amber" onClick={() => handleFlag(event)} disabled={busy}>🚩 Flag</Btn>
                  )}
                  <Btn variant="reject" onClick={() => handleRemove(event)} disabled={busy}>Remove</Btn>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STATS BAR
// ══════════════════════════════════════════════════════════════════════════════
function StatsBar({ flaggedCount, isMobile }) {
  const [stats, setStats] = useState({ total: 0, upcoming: 0, hosting: 0 });

  useEffect(() => {
    async function load() {
      // onlyUpcoming rather than .gte('date', today) built from
      // new Date().toISOString(), which is UTC — so all evening in Pacific it
      // had already rolled to tomorrow and dropped today's events — and which
      // also ends a multi-day event on its start date instead of its end date.
      const [{ count: total }, { count: upcoming }, { count: hosting }] = await Promise.all([
        supabase.from('profile_events').select('*', { count: 'exact', head: true }),
        onlyUpcoming(supabase.from('profile_events').select('*', { count: 'exact', head: true })),
        supabase.from('profile_events').select('*', { count: 'exact', head: true }).eq('role', 'hosting'),
      ]);
      setStats({ total: total || 0, upcoming: upcoming || 0, hosting: hosting || 0 });
    }
    load();
  }, []);

  const cards = [
    { label: 'Total events',    value: stats.total,   accent: '#2563eb', tip: 'Every event row, past and upcoming, including flagged ones.' },
    { label: 'Upcoming',        value: stats.upcoming, accent: '#16a34a', tip: COUNT_TIPS.upcomingEvents },
    { label: 'Readers hosting', value: stats.hosting,  accent: '#d97706', tip: 'Events where the reader marked themselves as hosting rather than attending. Counts events, not readers.' },
    { label: 'Flagged',         value: flaggedCount,   accent: '#dc2626', tip: 'Events a community member reported. Counts events, not reports.' },
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? 10 : 12,
      marginBottom: 22,
    }}>
      {cards.map(c => (
        <div key={c.label} style={{
          background: 'var(--brand-white)', border: '1px solid var(--brand-sand)',
          borderTop: `3px solid ${c.accent}`, borderRadius: 'var(--radius-lg)',
          padding: isMobile ? '12px 14px' : '16px 18px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              {c.label}
            </span>
            <InfoTip text={c.tip} />
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 22 : 28, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -1, lineHeight: 1 }}>
            {c.value.toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════════════════════
export default function EventsSection({ onBadgeChange, isMobile }) {
  const [tab, setTab]               = useState('flagged');
  const [flaggedCount, setFlaggedCount] = useState(0);
  function handleFlaggedCount(n) {
    setFlaggedCount(n);
    onBadgeChange?.('events', n);
  }

  const TABS = [
    { id: 'flagged', label: flaggedCount > 0 ? `Flagged (${flaggedCount})` : 'Flagged', highlight: flaggedCount > 0 },
    { id: 'all',     label: 'All events' },
  ];

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '30px 36px', fontFamily: 'var(--font-body)' }}>
      <div style={{ marginBottom: 26 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 20 : 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, lineHeight: 1.1, margin: 'var(--space-none)' }}>
          Events
        </h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 'var(--space-xs)', lineHeight: 1.5 }}>
          {isMobile
            ? 'Review and moderate community events'
            : 'Events auto-post to community feed and drop a pin on the map · flagged events need review'}
        </p>
      </div>

      <StatsBar flaggedCount={flaggedCount} isMobile={isMobile} />

      <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '14px' : '20px 22px' }}>
        {/* Tab bar */}
        <div style={{
          display: 'flex', borderBottom: '2px solid var(--brand-sand)',
          marginBottom: 18, marginTop: -4,
          overflowX: 'auto', WebkitOverflowScrolling: 'touch',
        }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              fontSize: 'var(--font-size-sm)', fontWeight: 600,
              padding: isMobile ? '8px 12px' : '8px 16px',
              color: tab === t.id ? 'var(--neutral-800)' : t.highlight ? 'var(--feedback-error)' : 'var(--neutral-400)',
              border: 'none', background: 'transparent',
              borderBottom: tab === t.id ? '2px solid var(--neutral-800)' : '2px solid transparent',
              marginBottom: -2, cursor: 'pointer', fontFamily: 'var(--font-body)',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'flagged' && <FlaggedQueue onCountChange={handleFlaggedCount} isMobile={isMobile} />}
        {tab === 'all'     && <AllEvents isMobile={isMobile} />}
      </div>
    </div>
  );
}